import type { Provider, TestResult } from "./types";
import { connectionsApi } from "@/lib/api";
import { EmailBisonIcon } from "@/components/providers/icons/EmailBisonIcon";

/**
 * EmailBison provider implementation
 */
export const emailBisonProvider: Provider = {
  id: "emailbison",
  type: "emailbison",
  name: "EmailBison",
  description:
    "Connect to EmailBison to manage your email campaigns and automation.",
  icon: EmailBisonIcon,
  supportsAutoReply: true,
  configFields: [
    {
      name: "apiKey",
      label: "API Key",
      type: "password",
      required: true,
      placeholder: "Enter your EmailBison API key",
      description:
        "Find this in your EmailBison [API settings](https://send.listmavericks.com/settings/api)",
    },
    {
      name: "workspace",
      label: "Workspace",
      type: "text",
      required: true,
      placeholder: "Enter your workspace name",
      description:
        "Find this in your EmailBison [Workspace settings](https://send.listmavericks.com/team)",
    },
    {
      name: "isActive",
      label: "Active",
      type: "toggle",
      required: false,
      description: "Enable or disable this connection",
    },
  ],
  validateConfig: async (config) => {
    if (!config.apiKey || !config.workspace) {
      return false;
    }
    return true;
  },
  testConnection: async (config): Promise<TestResult> => {
    try {
      const result = await connectionsApi
        .testConnection({
          sourceType: "emailbison",
          apiKey: config.apiKey as string,
          workspace: config.workspace as string,
          isActive: config.isActive as boolean,
          autoReply: config.autoReply as boolean,
          minReplyDelaySeconds: config.minReplyDelaySeconds as number,
          maxReplyDelaySeconds: config.maxReplyDelaySeconds as number,
        })
        .catch((error) => {
          console.error(error);
          return {
            success: false,
            message:
              error instanceof Error ? error.message : "Connection failed",
          };
        });

      return {
        success: result.success === true,
        message: result.message || "Connection successful",
      };
    } catch (error) {
      console.error(error);

      // Handle 400 status code specifically for invalid config
      if (error instanceof Error && "status" in error && error.status === 400) {
        return {
          success: false,
          message:
            "Invalid configuration. Please check your API key and workspace settings.",
        };
      }

      return {
        success: false,
        message: error instanceof Error ? error.message : "Connection failed",
      };
    }
  },
};
